import type { SpotDistanceSegment, Terrain, TerrainBase } from '@firesim/simkit-core';

export enum TerrainCodes {
    Empty = 0,
    Edges = 1, // Clean these up
    GrassShort = 2,
    Dirt = 3,
    Road= 4,
    House = 5,
    ForestLow = 6,
    GrassCut = 7,
    GrassNatural = 8,
    GrassEaten = 9,
    Water = 10,
    ForestHigh = 12,
    ForestMed = 13,
};
// Add a type-safe way to retrieve enum keys or values:
export type TerrainCodeKey = keyof typeof TerrainCodes;
//export type TerrainCodeValue = typeof TerrainCodes[TerrainCodeKey];
export type TerrainCodeValue = number;

export const terrainCodeValues: TerrainCodes[] = Object.values(TerrainCodes).filter(
  (value) => typeof value === 'number'
) as TerrainCodes[];

const spotDistanceTemplates: Record<string, Array<SpotDistanceSegment>> = {
    grass: [{
        weight: 0.70,
        minDistance: 0.5,
        maxDistance: 3
    },
    {
        weight: 0.20,
        minDistance: 0.5,
        maxDistance: 4
    }],
    scrub: [{
        weight: 0.011,
        minDistance: 1,
        maxDistance: 4
    },
    {
        weight: 0.011,
        minDistance: 0,
        maxDistance: 10
    }],
    canopy: [{
        weight: 0.011,
        minDistance: 1,
        maxDistance: 4
    },
    {
        weight: 0.011,
        minDistance: 5,
        maxDistance: 250
    }]
};

export const terrainTypes: Record<TerrainCodes, Terrain<TerrainCodes>> = {
  [TerrainCodes.Empty]: {
    code: TerrainCodes.Empty,
    colour: {
      red: 255,
      green: 255,
      blue: 255,
    },
    fuelLoad: 0

  },
  [TerrainCodes.House]: {
    code: TerrainCodes.House,
    colour: {
      red: 92,
      green: 65,
      blue: 50,
    },
    fuelLoad: 0.6
  },
  [TerrainCodes.Road]: {
    code: TerrainCodes.Road,
    colour: {
      red: 128,
      green: 128,
      blue: 128,
    },
    fuelLoad: 0
  },
  [TerrainCodes.GrassShort]: {
    code: TerrainCodes.GrassShort,
    colour: {
      red: 124,
      green: 252,
      blue: 0,
    },
    fuelLoad: 0.94,
    spotDistanceDistribution: spotDistanceTemplates.grass
  },
  [TerrainCodes.GrassCut]: {
    code: TerrainCodes.GrassCut,
    colour: {
      red: 102,
      green: 205,
      blue: 170,
    },
    fuelLoad: 0.96,
    spotDistanceDistribution: spotDistanceTemplates.grass
  },
  [TerrainCodes.GrassNatural]: {
    code: TerrainCodes.GrassNatural,
    colour: {
      red: 73,
      green: 110,
      blue: 78,
    },
    fuelLoad: 1,
    spotDistanceDistribution: spotDistanceTemplates.grass
  },
  [TerrainCodes.GrassEaten]: {
    code: TerrainCodes.GrassEaten,
    colour: {
      red: 189,
      green: 183,
      blue: 107,
    },
    fuelLoad: 0.9,
    spotDistanceDistribution: spotDistanceTemplates.grass
  },
  [TerrainCodes.ForestLow]: {
    code: TerrainCodes.ForestLow,
    colour: {
      red: 85,
      green: 107,
      blue: 47,
    },
    fuelLoad: 0.95,
    fuelLoadFuzzRange: 0.1,
    spotDistanceDistribution: spotDistanceTemplates.scrub
  },
  [TerrainCodes.ForestHigh]: {
    code: TerrainCodes.ForestHigh,
    colour: {
      red: 34,
      green: 139,
      blue: 34,
    },
    fuelLoad: 0.98,
    spotDistanceDistribution: spotDistanceTemplates.canopy
  },
  [TerrainCodes.ForestMed]: {
    code: TerrainCodes.ForestMed,
    colour: {
      red: 34,
      green: 139,
      blue: 34,
    },
    fuelLoad: 0.97,
    spotDistanceDistribution: spotDistanceTemplates.canopy
  },
  [TerrainCodes.Dirt]: {
    code: TerrainCodes.Dirt,
    colour: {
      red: 196,
      green: 168,
      blue: 122,
    },
    fuelLoad: 0.4,
    fuelLoadFuzzRange: 0.1
  },
  [TerrainCodes.Water]: {
    code: TerrainCodes.Water,
    colour: {
      red: 0,
      green: 105,
      blue: 148,
    },
    fuelLoad: 0
  },
  [TerrainCodes.Edges]: {
    code: TerrainCodes.Edges,
    colour: {
      red: 169,
      green: 169,
      blue: 169,
    },
    fuelLoad: 0.1
  }
};

export const terrain: (typeof terrainTypes)[TerrainCodes][] = Object.values(terrainTypes);
